import { AdminUser } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.dev.auth.isaacmccourt.dev/v1';

// Debug logging to see what URL is being used
console.log('VITE_API_BASE_URL environment variable:', import.meta.env.VITE_API_BASE_URL);
console.log('Final API_BASE_URL being used:', API_BASE_URL);

class AdminAuthAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
    console.log('AdminAuthAPI initialized with baseUrl:', this.baseUrl);
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getStoredToken();
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  private getStoredToken(): string | null {
    return localStorage.getItem('admin_access_token');
  }

  private setStoredToken(token: string): void {
    localStorage.setItem('admin_access_token', token);
  }

  private getStoredRefreshToken(): string | null {
    return localStorage.getItem('admin_refresh_token');
  }

  private setStoredRefreshToken(token: string): void {
    localStorage.setItem('admin_refresh_token', token);
  }

  private clearStoredToken(): void {
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_refresh_token');
  }

  async signIn(email: string, password: string, mfaCode?: string): Promise<{
    user: AdminUser;
    accessToken: string;
    requiresMfa?: boolean;
  }> {
    const response = await this.request<{
      message: string;
      accessToken: string;
      refreshToken: string;
      user: AdminUser;
      requiresMfa?: boolean;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        mfaCode,
      }),
    });

    if (response.accessToken) {
      this.setStoredToken(response.accessToken);
    }
    if (response.refreshToken) {
      this.setStoredRefreshToken(response.refreshToken);
    }

    return {
      user: response.user,
      accessToken: response.accessToken,
      requiresMfa: response.requiresMfa,
    };
  }

  async signOut(): Promise<void> {
    try {
      const refreshToken = this.getStoredRefreshToken();
      await this.request<{ message: string }>('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } finally {
      this.clearStoredToken();
    }
  }

  async refreshToken(): Promise<{ accessToken: string }> {
    const refreshToken = this.getStoredRefreshToken();
    const response = await this.request<{
      message: string;
      accessToken: string;
      refreshToken: string;
    }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });

    if (response.accessToken) {
      this.setStoredToken(response.accessToken);
    }
    if (response.refreshToken) {
      this.setStoredRefreshToken(response.refreshToken);
    }

    return { accessToken: response.accessToken };
  }

  async getProfile(): Promise<AdminUser> {
    // The backend returns the user unwrapped (`{ user }`), not the APIResponse
    // envelope (`{ data: { user } }`).
    const response = await this.request<{ user: AdminUser }>('/users/profile');
    return response.user;
  }

  async verifyToken(): Promise<boolean> {
    try {
      await this.getProfile();
      return true;
    } catch {
      this.clearStoredToken();
      return false;
    }
  }
}

export const adminAuthAPI = new AdminAuthAPI();
